import { Compass, Ticket, Hotel, Wallet, BookOpen, CalendarDays, FileText, Plane } from "lucide-react";

const EmptyStateWrapper = ({ icon: Icon, iconColor, iconBg, title, description, actionLabel, onAction, testId }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center animate-fade-in-up" data-testid={testId || "empty-state"}>
    {/* Animated illustration */}
    <div className="relative mb-6">
      <div className={`w-20 h-20 rounded-3xl ${iconBg} flex items-center justify-center shadow-lg`} style={{ animation: 'icon-float 3s ease-in-out infinite' }}>
        <Icon className={`w-9 h-9 ${iconColor}`} />
      </div>
      {/* Decorative rings */}
      <div className={`absolute inset-0 rounded-3xl border ${iconColor.replace('text-', 'border-')}/20 scale-[1.35]`} style={{ animation: 'icon-pulse 2s ease-in-out infinite' }} />
      <div className={`absolute inset-0 rounded-3xl border ${iconColor.replace('text-', 'border-')}/10 scale-[1.7]`} style={{ animation: 'icon-pulse 2s ease-in-out 0.5s infinite' }} />
    </div>
    <h3 className="text-lg font-semibold font-heading text-white mb-2">{title}</h3>
    <p className="text-sm text-slate-500 max-w-xs mb-6 leading-relaxed">{description}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="btn-primary text-sm"
        data-testid={testId ? `${testId}-action` : "empty-state-action"}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export const EmptyTrips = ({ onAction }) => (
  <EmptyStateWrapper
    icon={Compass}
    iconColor="text-cyan-400"
    iconBg="bg-gradient-to-br from-cyan-500/20 to-sky-500/10"
    title="No trips yet"
    description="Your adventure starts here. Create your first trip and start building your perfect itinerary."
    actionLabel="Create Your First Trip"
    onAction={onAction}
    testId="empty-trips"
  />
);

export const EmptyTickets = ({ onAction }) => (
  <EmptyStateWrapper
    icon={Ticket}
    iconColor="text-amber-400"
    iconBg="bg-gradient-to-br from-amber-500/20 to-orange-500/10"
    title="No tickets yet"
    description="Add your flight tickets, train passes, or event tickets to keep them all in one place."
    actionLabel="Add Your First Ticket"
    onAction={onAction}
    testId="empty-tickets"
  />
);

export const EmptyBookings = ({ onAction }) => (
  <EmptyStateWrapper
    icon={Hotel}
    iconColor="text-rose-400"
    iconBg="bg-gradient-to-br from-rose-500/20 to-pink-500/10"
    title="No bookings yet"
    description="Store your hotel reservations, Airbnb stays, and accommodation details here."
    actionLabel="Add Your First Booking"
    onAction={onAction}
    testId="empty-bookings"
  />
);

export const EmptyBudget = ({ onAction }) => (
  <EmptyStateWrapper
    icon={Wallet}
    iconColor="text-emerald-400"
    iconBg="bg-gradient-to-br from-emerald-500/20 to-teal-500/10"
    title="No expenses tracked"
    description="Start tracking your travel spending. Set a budget and log expenses to stay on top of costs."
    actionLabel="Add First Expense"
    onAction={onAction}
    testId="empty-budget"
  />
);

export const EmptyJournal = ({ onAction }) => (
  <EmptyStateWrapper
    icon={BookOpen}
    iconColor="text-purple-400"
    iconBg="bg-gradient-to-br from-purple-500/20 to-violet-500/10"
    title="No journal entries yet"
    description="Capture your travel memories. Write about the places you've been, the people you've met, and the experiences you've had."
    actionLabel="Write Your First Entry"
    onAction={onAction}
    testId="empty-journal"
  />
);

export const EmptyCalendar = () => (
  <EmptyStateWrapper
    icon={CalendarDays}
    iconColor="text-violet-400"
    iconBg="bg-gradient-to-br from-violet-500/20 to-purple-500/10"
    title="No events this month"
    description="Plan activities, set reminders for travel dates, and keep track of your schedule."
    testId="empty-calendar"
  />
);

export const EmptyDocuments = ({ onAction }) => (
  <EmptyStateWrapper
    icon={FileText}
    iconColor="text-sky-400"
    iconBg="bg-gradient-to-br from-sky-500/20 to-blue-500/10"
    title="No documents yet"
    description="Store your passport details, visa copies, and other travel documents securely."
    actionLabel="Add Document"
    onAction={onAction}
    testId="empty-documents"
  />
);
